interface Summary { comments: number; candidates: number; clusters: number; captures: number; }
interface Candidate {
  id: string;
  pain_summary: string | null;
  canonical_title: string | null;
  target_user: string | null;
  scenario: string | null;
  severity: number;
  urgency: number;
  payment_signal: string;
  confidence: number;
  candidate_score: number;
  content: string | null;
  source_url: string;
}
interface Cluster { id: string; canonical_title: string; status: string; score: number; comment_count: number; target_user: string | null; scenario: string | null; }
interface Watchlist { id: string; kind: string; value: string; is_active: number; }

const login = document.querySelector<HTMLElement>("#login")!;
const app = document.querySelector<HTMLElement>("#app")!;
const loginForm = document.querySelector<HTMLFormElement>("#login-form")!;
const loginError = document.querySelector<HTMLElement>("#login-error")!;
const metrics = document.querySelector<HTMLElement>("#metrics")!;
const candidates = document.querySelector<HTMLElement>("#candidates")!;
const clusters = document.querySelector<HTMLElement>("#clusters")!;
const watchlist = document.querySelector<HTMLElement>("#watchlist")!;
const tokenResult = document.querySelector<HTMLElement>("#token-result")!;

function escapeHtml(value: string | null | undefined): string {
  return (value ?? "—").replace(/[&<>'\"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character] ?? character);
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, { ...init, headers: { "content-type": "application/json", ...(init.headers ?? {}) } });
  const body = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) throw new Error(body.error ?? "请求失败。");
  return body;
}

function renderMetrics(summary: Summary): void {
  const values = [["评论", summary.comments], ["待审核", summary.candidates], ["需求簇", summary.clusters], ["已采集笔记", summary.captures]];
  metrics.innerHTML = values.map(([label, value]) => `<div class="metric"><span class="muted">${label}</span><strong>${value}</strong></div>`).join("");
}

function renderCandidates(rows: Candidate[]): void {
  if (rows.length === 0) { candidates.textContent = "暂无待审核候选。"; return; }
  candidates.innerHTML = `<table><thead><tr><th>需求</th><th>场景 / 用户</th><th>评分</th><th>操作</th></tr></thead><tbody>${rows.map((row) => `<tr><td><strong>${escapeHtml(row.canonical_title ?? row.pain_summary)}</strong><br/><span class="muted">${escapeHtml(row.content)}</span><br/><a href="${escapeHtml(row.source_url)}" target="_blank" rel="noreferrer">查看来源</a></td><td>${escapeHtml(row.scenario)}<br/><span class="muted">${escapeHtml(row.target_user)}</span></td><td>${row.candidate_score}<br/><span class="muted">严重度 ${row.severity} · ${escapeHtml(row.payment_signal)}</span></td><td class="actions"><button class="primary" data-review="confirm" data-id="${row.id}">确认</button><button class="secondary" data-review="reject" data-id="${row.id}">驳回</button></td></tr>`).join("")}</tbody></table>`;
  candidates.querySelectorAll<HTMLButtonElement>("[data-review]").forEach((button) => button.addEventListener("click", () => {
    void (async () => {
      await api("/api/reviews", { method: "POST", body: JSON.stringify({ analysisId: button.dataset.id, decision: button.dataset.review }) });
      await loadDashboard();
    })().catch((cause) => window.alert(cause instanceof Error ? cause.message : "审核失败。"));
  }));
}

function renderClusters(rows: Cluster[]): void {
  if (rows.length === 0) { clusters.textContent = "暂无需求簇。"; return; }
  clusters.innerHTML = `<table><thead><tr><th>需求簇</th><th>用户 / 场景</th><th>状态</th><th>评分</th></tr></thead><tbody>${rows.map((row) => `<tr><td><strong>${escapeHtml(row.canonical_title)}</strong></td><td>${escapeHtml(row.target_user)}<br/><span class="muted">${escapeHtml(row.scenario)}</span></td><td>${escapeHtml(row.status)}</td><td>${row.score} / ${row.comment_count} 条</td></tr>`).join("")}</tbody></table>`;
}

function renderWatchlist(rows: Watchlist[]): void {
  watchlist.innerHTML = rows.length ? `<table><thead><tr><th>类型</th><th>观察项</th><th>状态</th></tr></thead><tbody>${rows.map((row) => `<tr><td>${escapeHtml(row.kind)}</td><td>${escapeHtml(row.value)}</td><td>${row.is_active ? "启用" : "停用"}</td></tr>`).join("")}</tbody></table>` : "暂无观察项。";
}

async function loadDashboard(): Promise<void> {
  const [summary, candidateResponse, clusterResponse, watchlistResponse] = await Promise.all([
    api<Summary>("/api/dashboard/summary"),
    api<{ candidates: Candidate[] }>("/api/candidates"),
    api<{ clusters: Cluster[] }>("/api/clusters"),
    api<{ watchlist: Watchlist[] }>("/api/watchlist")
  ]);
  renderMetrics(summary);
  renderCandidates(candidateResponse.candidates);
  renderClusters(clusterResponse.clusters);
  renderWatchlist(watchlistResponse.watchlist);
}

async function restoreSession(): Promise<void> {
  try {
    await api<{ email: string }>("/api/auth/me");
    login.classList.add("hidden");
    app.classList.remove("hidden");
    await loadDashboard();
  } catch {
    login.classList.remove("hidden");
    app.classList.add("hidden");
  }
}

loginForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void (async () => {
    const email = document.querySelector<HTMLInputElement>("#email")!.value;
    const password = document.querySelector<HTMLInputElement>("#password")!.value;
    await api("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    loginError.textContent = "";
    await restoreSession();
  })().catch((cause) => { loginError.textContent = cause instanceof Error ? cause.message : "登录失败。"; });
});

document.querySelector<HTMLFormElement>("#bootstrap-form")!.addEventListener("submit", (event) => {
  event.preventDefault();
  const bootstrapError = document.querySelector<HTMLElement>("#bootstrap-error")!;
  void (async () => {
    await api("/api/auth/bootstrap", {
      method: "POST",
      body: JSON.stringify({
        email: document.querySelector<HTMLInputElement>("#bootstrap-email")!.value,
        password: document.querySelector<HTMLInputElement>("#bootstrap-password")!.value,
        bootstrapToken: document.querySelector<HTMLInputElement>("#bootstrap-token")!.value
      })
    });
    bootstrapError.textContent = "初始化完成，请使用新账号登录。";
  })().catch((cause) => { bootstrapError.textContent = cause instanceof Error ? cause.message : "初始化失败。"; });
});

document.querySelector<HTMLButtonElement>("#refresh")!.addEventListener("click", () => { void loadDashboard(); });
document.querySelector<HTMLButtonElement>("#logout")!.addEventListener("click", () => { void api("/api/auth/logout", { method: "POST" }).finally(() => void restoreSession()); });
document.querySelector<HTMLButtonElement>("#create-token")!.addEventListener("click", () => {
  void api<{ token: string }>("/api/tokens", { method: "POST", body: JSON.stringify({ label: "Chrome Extension" }) })
    .then((result) => { tokenResult.textContent = result.token; })
    .catch((cause) => window.alert(cause instanceof Error ? cause.message : "创建失败。"));
});
document.querySelector<HTMLButtonElement>("#revoke-tokens")!.addEventListener("click", () => {
  if (!window.confirm("确定吊销所有 Extension Token 吗？")) return;
  void api("/api/tokens", { method: "DELETE" }).then(() => { tokenResult.textContent = "已吊销全部 Token。"; });
});
document.querySelector<HTMLFormElement>("#watchlist-form")!.addEventListener("submit", (event) => {
  event.preventDefault();
  const kind = document.querySelector<HTMLSelectElement>("#watchlist-kind")!.value;
  const value = document.querySelector<HTMLInputElement>("#watchlist-value")!;
  void api("/api/watchlist", { method: "POST", body: JSON.stringify({ kind, value: value.value }) })
    .then(async () => { value.value = ""; await loadDashboard(); })
    .catch((cause) => window.alert(cause instanceof Error ? cause.message : "保存失败。"));
});

void restoreSession();
